import AddressDetails from "@/components/ReviewApplication/AddressDetails";
import CoapplicantDetails from "@/components/ReviewApplication/CoapplicantDetails";
import EmploymentDetails from "@/components/ReviewApplication/EmploymentDetails";
import MortgageDetails from "@/components/ReviewApplication/MortgageDetails";
import PersonalDetails from "@/components/ReviewApplication/PersonalDetails";
import PreviousAddressDetails from "@/components/ReviewApplication/PreviousAddressDetails";
import PropertyDetails from "@/components/ReviewApplication/PropertyDetails";
import { useAuth } from "@/context/AuthContext";
import { AddApplicantConsentCommand, ApplicantForBlockList, ApplicationDto, TokenData } from "@/lib/DTMWebAPIClient.generated";
import { createIRSApplication, IDRequestStatus } from "@/lib/irs";
import { createApiClient } from "@/lib/Requests";
import { setClickInsightEvents } from "@/lib/util";
import { setConsentCompleted, setIdVerificationStatus } from "@/slices/applicantSlice";
import { setApplicationEndpointReached, setApplicationProgress, setBlResult } from "@/slices/applicationSlice";
import { selectCoApplicant } from "@/slices/coApplicantSlice";
import { selectPreviousAddress } from "@/slices/PreviousAddressSlice";
import { setHomeDetailsComplete } from "@/slices/progressSlice";
import { selectPropertyInfo } from "@/slices/propertyInfoSlice";
import { RootState } from "@/store/dtm";
import SlideTransition from "@/styles/animation/SlideTransition";
import style from "@/styles/pages/ReviewApplication/ReviewApplication.module.css";
import { applicantType } from "@/types/applicantType";
import { verificationCategory } from "@/types/applicationType";
import { ICreateIRSRequest } from "@/types/irs/idRequestType";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

export default function JointReviewApplication() {
  const { login } = useAuth();
  const [addEdit, setAddEdit] = useState(false);
  const [consent, setConsent] = useState(false);
  const [profileEdit, setProfileEdit] = useState(false);
  const [employmentEdit, setEmploymentEdit] = useState(false);
  const [mortgageEdit, setMortgageEdit] = useState(false);
  const [propertyEdit, setPropertyEdit] = useState(false);
  const [submissionInProgress, setSubmissionInProgress] = useState(false);
  const [inAdequateAddressHistory, setInAdequateAddressHistory] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const applicant = useSelector(
    (state: RootState) => state.applicant.applicant
  );
  const credential = useSelector(
    (state: RootState) => state.credential
  );
  const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

  const application = useSelector(
    (state: RootState) => state.application.application
  );
  const homeAddress = useSelector(
    (state: RootState) => state.homeAddress.address
  );
  const mailingAddress = useSelector(
    (state: RootState) => state.mailingAddress.address
  );
  const previousAddresses = useSelector(selectPreviousAddress).addresses;
  const employment = useSelector(
    (state: RootState) => state.employment.employment
  );
  const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
  const isInAdequateAddressHistory = (data: boolean) => {
    setInAdequateAddressHistory(data);
  };

  const isAddEdit = (data: boolean) => {
    setAddEdit(data);
  };
  const isEmploymentEdit = (data: boolean) => {
    setEmploymentEdit(data);
  };
  const isProfileEdit = (data: boolean) => {
    setProfileEdit(data);
  };
  const handleConsent = (e: any) => {
    setConsent(e.target.checked);
  };

  async function handleClick() {
    //Prevents re-submission of application if the user goes back using browser navigation
    if (!applicant.isProspect && application.applicationEndpointReached){
      if (application.blResult){
        navigate("/single/application-error");
      }else{
        navigate("/joint/confirm-application");
      }
      return;
    }
    //Perform blocklist check
    setSubmissionInProgress(true);
    await checkIDVStatus(applicant);
  }

  useEffect(() => {
    updateAppData();
  }, [profileEdit, employmentEdit])

  const checkIDVStatus = async (applicant: applicantType) => {
    //Check the IDV Status in the table for this applicant (to see if it was thrown into hardfail by other applicant in the deal)
    //If at least one application has a hardfail, redirect to error page (since deal is already cancelled)
    await client.apiIdverificationIdverificationapplicant().then(async idVerification => {
      if (idVerification && idVerification.status == "Hardfail") {
        dispatch(setIdVerificationStatus(verificationCategory.Hardfail));
        dispatch(setApplicationEndpointReached(true));
        navigate("/single/application-error");
      } else {
        checkBlockList(applicant);
      }
    }).catch((error) => {
      navigate("/error/3");
    });
  }

  const checkBlockList = async (applicant: applicantType) => {
    const request: ApplicantForBlockList = new ApplicantForBlockList({
      firstName: applicant.firstName,
      lastName: applicant.lastName,
      birthDate: `${applicant.dob_year}-${applicant.dob_month}-${applicant.dob_day.length > 1
        ? applicant.dob_day
        : "0" + applicant.dob_day
        }`,
      alloySessionId: applicant.alloySessionId,
      applicationId: application.id,
      alloySessionCreated: false
    });

    await client.apiIdverificationBlocklistcheck(request)
      .then(async (data) => {
        let blocklistResult = false;
        if (data?.blockListFound) {
          dispatch(setBlResult(true));
          blocklistResult = true;
        }

        if (applicant.isProspect) {
          updateConsent();
        } else {
          const idRequest = buildIdRequest();
          idRequest.additionalData.blocklist = blocklistResult;

          const response = await createIRSApplication(idRequest);
          request.alloySessionCreated = true;
          await client.apiIdverificationBlocklistcheck(request);

          if (response?.idRequestStatus.toLowerCase() === IDRequestStatus.Approved) {
            dispatch(setIdVerificationStatus(verificationCategory.Pass));
            updateConsent();
          } else if (response?.idRequestStatus.toLowerCase() === IDRequestStatus.Denied) {
            dispatch(setIdVerificationStatus(verificationCategory.Hardfail));
            dispatch(setApplicationEndpointReached(true));
            navigate("/single/application-error");
          } else {
            setSubmissionInProgress(false);
            navigate("/error/3");
          }
        }
      })
      .catch((error) => {
        navigate("/error/1");
      });
  }

  const buildIdRequest = (): ICreateIRSRequest => {
    const addressList = [homeAddress];
    if (mailingAddress.postalCode) {
      addressList.push(mailingAddress);
    }

    return {
      applicant,
      credential: credential.credential,
      additionalData: {
        blocklist: application.blResult,
        phoneverified: applicant.twilioResult.codeVerified,
        isprospect: false
      },
      addresses: addressList,
    };
  };

  useEffect(() => {
    document.title = "Review your application";

    setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);
    dispatch(setHomeDetailsComplete(true));

    if (applicant.consentCompleted) {
      setConsent(true);
    }

    //Refresh Token if it is expired
    if (credential.credential.expiration && dayjs(credential.credential.expiration).diff(dayjs(), 'minute') < 1) {
      client.apiAuthRefreshtoken(new TokenData({
        accessToken: credential.credential.accessToken,
        refreshToken: credential.credential.refreshToken
      })).then((result) => {
        login(result.accessToken != undefined ? result.accessToken : "", result.refreshToken != undefined ? result.refreshToken : "");
        updateAppData();
      })
    } else {
      updateAppData();
    }
  }, []);

  const updateAppData = () => {
    client.apiApplicationsFetch().then((result) => {
      result.application = application;
      result.applicant = applicant;
      result.employment = employment;
      client.apiApplicationsSave(new ApplicationDto({
        applicationData: JSON.stringify(result)
      })).catch((error) => {
        console.log("Error saving application info");
      });
    }).catch((error) => {
      console.log("Error fetching application info");
    });
  }

  const updateConsent = () => {
    if (!applicant.consentCompleted) {
      client.apiDealsApplicantConsentPost(new AddApplicantConsentCommand({
        applicantId: applicant.id,
        channel: "Online"
      })).then((result) => {
        console.log("Consent updated successfully");
        dispatch(setConsentCompleted(true));
        dispatch(setApplicationProgress(15));
        if (applicant.isProspect) {
          navigate("/joint/legal-terms")
        } else {
          if (application.applicationEndpointReached == false) {
            dispatch(setApplicationEndpointReached(true));
          }
          navigate("/joint/confirm-application");
        }
      }).catch((error) => {
        console.log("Error updating consent", error);
        setSubmissionInProgress(false);
      });
    } else {
      dispatch(setApplicationProgress(15));
      if (applicant.isProspect) {
        navigate("/joint/legal-terms")
      } else {
        if (application.applicationEndpointReached == false) {
          dispatch(setApplicationEndpointReached(true));
        }
        navigate("/joint/confirm-application");
      }
    }
  }

  return (
    <SlideTransition>
      <main aria-labelledby="main-content">
        <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
          <div className={style.raHeading}>
            <h1>Review your application</h1>
            <p className="sub-heading">
              Please make sure that all your information is correct. When you're done, select {applicant.isProspect ? "'Continue'" : "'Submit'"}.
            </p>
            <div className={style.sectionContainer}>
              <hr className="mt-5" />
              <MortgageDetails editModeOn={false} restrictEditing={true} />
              <hr className="mt-5" />
              <PersonalDetails editModeOn={isProfileEdit} />
              <hr className="mt-5" />
              <EmploymentDetails editModeOn={isEmploymentEdit} />
              <AddressDetails editModeOn={isAddEdit} inAdequateAddressHistory={isInAdequateAddressHistory} />
              {previousAddresses && previousAddresses.length > 0 &&
                <PreviousAddressDetails editModeOn={isAddEdit} />
              }
              <hr className="mt-5" />
              <PropertyDetails editModeOn={false} restrictEditing={true} />
              <hr className="mt-5" />

              <div
                className={clsx("mt-5 flex-row-reverse", style.consentContainer)}
              >
                <input
                  type="checkbox"
                  className="accent"
                  id="check1"
                  checked={consent}
                  onChange={handleConsent}
                />
                <label htmlFor="check1">
                  <h3 className="sub-heading ml-7 -mt-5">
                    By checking this box and selecting continue:
                  </h3>
                </label>

                <table className="mt-5">
                  <tbody>
                    <tr>
                      <th style={{ verticalAlign: 'top' }}><p>1.</p></th>
                      <th><p className="sub-heading  ml-3">I understand that Meridian Credit Union is required to verify my identity and pull a credit report,
                        and that this may require sharing the information I provide on my application with third party service providers. By submitting this application,
                        I am giving Meridian Credit Union my consent to check my credit history and share my information with these Identity and Credit Verifiers.</p></th>
                    </tr>
                    <tr>
                      <td style={{ verticalAlign: 'top' }}><p>2.</p></td>
                      <td><p className="sub-heading  ml-3">
                        You agree to the{" "}
                        <a className="brand-blue-text underline cursor-pointer" href="https://www.meridiancu.ca/about-meridian/legal/electronic-services-agreement-terms-and-conditions" target="_blank">
                          Consent to Electronic Delivery of Documents
                        </a>{" "}
                        provided electronically through this website, or this mobile
                        application.
                      </p></td>
                    </tr>
                    <tr>
                      <td style={{ verticalAlign: 'top' }}><p>3.</p></td>
                      <td><p className="sub-heading  ml-3">You confirm this account will not be used by, or on behalf of, anyone not listed on this
                        application (i.e. a Third Party).</p></td>
                    </tr>
                    <tr>
                      <td style={{ verticalAlign: 'top' }}><p>4.</p></td>
                      <td><p className="sub-heading  ml-3">
                        You confirm that you have read and agree to Meridian's{" "}
                        <a className="brand-blue-text underline cursor-pointer" href="https://www.meridiancu.ca/about-meridian/privacy-and-security" target="_blank">
                          Privacy Statement
                        </a>{" "}
                        as it pertains to the collection and management of your
                        personal information.
                      </p></td>
                    </tr>
                    <tr>
                      <td style={{ verticalAlign: 'top' }}><p>5.</p></td>
                      <td><p className="sub-heading  ml-3">
                        I confirm the information I have provided is true and
                        complete, and I will keep it up to date. If anything I have
                        stated in this application changes, I will advise Meridian
                        Credit Union within 30 days.
                      </p></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="flex max-sm:hidden">
                <div className="mb-5 mr-5">
                  <button onClick={() => navigate(-1)}
                    type="button"
                    className="back mt-5 w-169 sm:w-28 h-11 px-6">
                    Back
                  </button>
                </div>
                <div>
                  <button
                    type="submit"
                    onClick={() => { handleClick() }}
                    disabled={profileEdit || addEdit || employmentEdit || mortgageEdit || propertyEdit || inAdequateAddressHistory || !consent || submissionInProgress}
                    className={clsx(
                      style.raHeading,
                      "mt-5 w-full sm:w-28 h-11 px-6",
                      profileEdit || addEdit || employmentEdit || mortgageEdit || propertyEdit || inAdequateAddressHistory || !consent
                        ? style.editMode
                        : "primary"
                    )}
                  >
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(submissionInProgress ? "" : "hidden")} />
                    {applicant.isProspect ? "Continue" : "Submit"}
                  </button>
                </div>
              </div>
              <div className="sm:hidden">
                <div className="mb-5">
                  <button
                    type="submit"
                    onClick={() => { handleClick() }}
                    disabled={profileEdit || addEdit || employmentEdit || mortgageEdit || propertyEdit || !consent || submissionInProgress}
                    className={clsx(
                      style.raHeading,
                      "mt-5 w-full sm:w-28 h-11 px-6",
                      profileEdit || addEdit || employmentEdit || mortgageEdit || propertyEdit || !consent
                        ? style.editMode
                        : "primary"
                    )}
                  >
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(submissionInProgress ? "" : "hidden")} />
                    {applicant.isProspect ? "Continue" : "Submit"}
                  </button>
                </div>
                <div className="mb-5">
                  <button onClick={() => navigate(-1)}
                    type="button"
                    className="back mt-5 w-full sm:w-28 h-11 px-6">
                    Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </SlideTransition>
  );
}